import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Send,
  Bot,
  User,
  Camera,
  Lock,
  MoreVertical,
  Check,
  X,
  Mic,
  MicOff,
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { assistantService } from '../services/assistantService';
import {
  chatHistoryService,
  ChatMessage,
} from '../services/chatHistoryService';
import { contextContinuationService } from '../services/contextContinuationService';
import { tokenCounterService } from '../services/tokenCounterService';
import { chatEncryptionService } from '../services/chatEncryptionService';
import { voiceTranscriptionService } from '../services/voiceTranscriptionService';
import { useDevConsoleStore } from '../stores/useDevConsoleStore';
import authProvider from '../authProvider';
import html2canvas from 'html2canvas';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface AssistantChatProps {
  children?: React.ReactNode;
}

export const AssistantChat: React.FC<AssistantChatProps> = ({ children }) => {
  const { translate, locale } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [userLastName, setUserLastName] = useState<string | null>(null);
  const [userUsername, setUserUsername] = useState<string | null>(null);
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [cryptoAvailable, setCryptoAvailable] = useState(false);
  const [currentContext, setCurrentContext] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const { transcriptionModel, chatModel } = useDevConsoleStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Initialize services on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        // Check if Web Crypto API is available
        const cryptoAvailable = chatEncryptionService.isAvailable();
        setCryptoAvailable(cryptoAvailable);

        // Check if encryption was previously enabled
        const savedEncryptionState = localStorage.getItem(
          'neko-assistant-encryption-enabled'
        );
        if (savedEncryptionState === 'true' && cryptoAvailable) {
          setEncryptionEnabled(true);
        }

        // Get current user
        const identity = await authProvider.getIdentity();
        const currentUserId = identity?.id || null;
        setUserId(currentUserId);

        // Fetch user profile to get firstName, lastName, username
        try {
          const profile = await authProvider.auth.getUserProfile();
          setUserFirstName(profile?.firstName || null);
          setUserLastName(profile?.lastName || null);
          setUserUsername((profile as any)?.username || null);
        } catch (error) {
          console.error('[AssistantChat] Error fetching user profile:', error);
        }

        if (currentUserId) {
          // Initialize services
          await chatHistoryService.initialize(currentUserId);
          await tokenCounterService.initialize();

          // Set initial context to current page
          const initialPage = window.location.pathname;
          setCurrentContext(initialPage);

          // Load chat history (only if we're on the same page as when it was saved)
          const history = await chatHistoryService.loadHistory(currentUserId);
          if (history) {
            setMessages(history.messages);
          }
        }
      } catch (error) {
        console.error('[AssistantChat] Initialization error:', error);
      }
    };

    initialize();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Detect context switches when sending messages
  // Context is checked in handleSend before including history

  // Handle send message
  const handleSend = async () => {
    if (!input.trim() || loading || !userId) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    // Add user message to UI immediately
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Save message and check for summarization
      await contextContinuationService.addMessageAndCheck(userId, userMessage);

      // Get current page context
      const currentPage = window.location.pathname;

      // Check if context has changed - if so, clear history
      const contextChanged =
        currentContext !== null && currentContext !== currentPage;
      if (contextChanged) {
        console.log(
          '[AssistantChat] Context switched, clearing chat history:',
          {
            oldContext: currentContext,
            newContext: currentPage,
          }
        );

        // Clear history for the new context
        await chatHistoryService.clearHistory(userId);
        setMessages([]);
        console.log(
          '[AssistantChat] Chat history cleared due to context switch'
        );

        // Update context to new page
        setCurrentContext(currentPage);
      } else if (currentContext === null) {
        // First message - set initial context
        setCurrentContext(currentPage);
      }

      const pageContext = {
        page: currentPage,
        userId,
        roles: (await authProvider.getIdentity())?.roles || [],
        locale: locale || 'en', // Pass user's language preference
      };

      // Only include conversation history if we're still in the same context
      // (i.e., same page as when the conversation started)
      // Note: After context change, we don't include history (fresh start)
      let conversationHistory: any[] = [];
      if (!contextChanged && currentContext === currentPage) {
        conversationHistory =
          await contextContinuationService.getConversationContext(userId);
        console.log('[AssistantChat] Including conversation history:', {
          historyLength: conversationHistory.length,
          context: currentPage,
        });
      } else {
        console.log('[AssistantChat] Not including history:', {
          contextChanged,
          currentContext,
          currentPage,
        });
      }

      // Ensure token is valid before making request
      const auth = authProvider.auth;
      const isTokenExpired = await auth.isTokenExpire();
      if (isTokenExpired) {
        console.log(
          '[AssistantChat] Token expired, refreshing before request...'
        );
        try {
          await auth.fetchRefreshToken();
          console.log('[AssistantChat] Token refreshed successfully');
        } catch (refreshError) {
          console.error('[AssistantChat] Token refresh failed:', refreshError);
          throw new Error('Authentication required. Please log in again.');
        }
      }

      // Call assistant API with conversation history
      // Note: Role simulation is handled globally via X-Simulated-Roles header
      // No need to pass roles here - the backend will use simulated roles if enabled
      const response = await assistantService.chat({
        message: userMessage.content,
        context: pageContext,
        history: conversationHistory, // Include conversation history
        chatModel: chatModel !== 'gpt-4o-mini' ? chatModel : undefined,
      });

      // Add assistant response with actions and suggestions
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || [],
        suggestions: response.suggestions || [],
      };

      setMessages((prev) => [...prev, assistantMessage]);
      await contextContinuationService.addMessageAndCheck(
        userId,
        assistantMessage
      );
    } catch (error) {
      console.error('[AssistantChat] Error sending message:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: translate(
          'assistant.errorMessage',
          'Sorry, I encountered an error. Please try again or contact support.'
        ),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Handle screenshot capture
  const handleScreenshot = async () => {
    if (!userId) return;

    try {
      // Capture the current page (excluding the chat panel)
      const canvas = await html2canvas(document.body, {
        ignoreElements: (element) => {
          // Exclude the chat sheet from screenshot
          return element.closest('[role="dialog"]') !== null;
        },
      });

      const screenshot = canvas.toDataURL('image/png');

      // Add a message indicating screenshot was captured
      const userMessage: ChatMessage = {
        role: 'user',
        content: translate(
          'assistant.screenshotCaptured',
          'Screenshot captured'
        ),
        screenshot: screenshot, // Store screenshot in message
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);

      // Save screenshot message to history
      await contextContinuationService.addMessageAndCheck(userId, userMessage);

      setLoading(true);

      // Send screenshot to assistant
      const pageContext = {
        page: window.location.pathname,
        userId,
        roles: (await authProvider.getIdentity())?.roles || [],
        locale: locale || 'en', // Pass user's language preference
      };

      const response = await assistantService.chat({
        message: 'What do you see in this screenshot?',
        context: pageContext,
        screenshot,
      });

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      await contextContinuationService.addMessageAndCheck(
        userId,
        assistantMessage
      );
    } catch (error) {
      console.error('[AssistantChat] Error capturing screenshot:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle clear history
  const handleClearHistory = async () => {
    if (!userId) return;
    const confirmMessage = translate(
      'assistant.clearHistoryConfirm',
      'Are you sure you want to clear chat history?'
    );
    if (!confirm(confirmMessage)) return;

    await chatHistoryService.clearHistory(userId);
    setMessages([]);
  };

  // Handle encryption toggle
  const handleEncryptionToggle = async (enabled: boolean) => {
    if (!userId || !cryptoAvailable) return;

    setEncryptionEnabled(enabled);
    localStorage.setItem(
      'neko-assistant-encryption-enabled',
      enabled ? 'true' : 'false'
    );

    // If enabling encryption, encrypt existing history
    if (enabled) {
      try {
        const history = await chatHistoryService.loadHistory(userId);
        if (history && history.messages.length > 0) {
          // Note: Full encryption implementation would require user password
          // For now, we just mark it as encrypted
          console.log('[AssistantChat] Encryption enabled for future messages');
        }
      } catch (error) {
        console.error('[AssistantChat] Error enabling encryption:', error);
      }
    }
  };

  // Handle action approval
  const handleApproveAction = async (
    actionId: string,
    toolName: string,
    toolArgs: any
  ) => {
    if (!userId || loading) return;

    setLoading(true);
    try {
      // Ensure token is valid before making request
      const auth = authProvider.auth;
      const isTokenExpired = await auth.isTokenExpire();
      if (isTokenExpired) {
        console.log(
          '[AssistantChat] Token expired, refreshing before action execution...'
        );
        await auth.fetchRefreshToken();
        console.log('[AssistantChat] Token refreshed successfully');
      }

      // Execute the action
      const result = await assistantService.executeAction(
        actionId,
        true,
        toolArgs
      );

      // Update the message to mark action as completed
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.actions && msg.actions.some((a) => a.actionId === actionId)) {
            return {
              ...msg,
              actions: msg.actions.map((a) =>
                a.actionId === actionId ? { ...a, status: 'completed' } : a
              ),
            };
          }
          return msg;
        })
      );

      // Add a confirmation message
      const confirmationMessage: ChatMessage = {
        role: 'assistant',
        content: translate(
          'assistant.actionCompleted',
          `Action completed successfully! ${result.message || ''}`
        ),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, confirmationMessage]);
      await contextContinuationService.addMessageAndCheck(
        userId,
        confirmationMessage
      );
    } catch (error) {
      console.error('[AssistantChat] Error executing action:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: translate(
          'assistant.actionError',
          'Failed to execute action. Please try again.'
        ),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Handle action rejection
  const handleRejectAction = async (actionId: string) => {
    if (!userId) return;

    // Update the message to mark action as rejected
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.actions && msg.actions.some((a) => a.actionId === actionId)) {
          return {
            ...msg,
            actions: msg.actions.map((a) =>
              a.actionId === actionId ? { ...a, status: 'rejected' } : a
            ),
          };
        }
        return msg;
      })
    );

    // Add a rejection message
    const rejectionMessage: ChatMessage = {
      role: 'assistant',
      content: translate(
        'assistant.actionRejected',
        'Action cancelled. How else can I help you?'
      ),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, rejectionMessage]);
    await contextContinuationService.addMessageAndCheck(
      userId,
      rejectionMessage
    );
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    // Optionally auto-send
    // handleSend();
  };

  // Handle voice recording
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        });

        try {
          setLoading(true);
          const result = await voiceTranscriptionService.transcribe({
            audioBlob,
            model: transcriptionModel,
          });
          setInput(result.text);
        } catch (error) {
          console.error('[AssistantChat] Transcription error:', error);
          const errorMessage: ChatMessage = {
            role: 'assistant',
            content: translate(
              'assistant.transcriptionError',
              'Failed to transcribe audio. Please try again.'
            ),
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
        } finally {
          setLoading(false);
          // Stop all tracks
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('[AssistantChat] Error starting recording:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: translate(
          'assistant.microphoneError',
          'Microphone access denied. Please enable microphone permissions.'
        ),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get greeting name: firstName > lastName > username
  const getGreetingName = (): string => {
    return userFirstName || userLastName || userUsername || '';
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children || (
          <Button
            variant="outline"
            size="icon"
            className="fixed bottom-20 right-4 lg:bottom-4 z-50 h-10 w-10"
          >
            <Bot className="h-4 w-4" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0"
      >
        <SheetHeader className="p-2.5 border-b space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreVertical className="h-3.5 w-3.5" />
                    <span className="sr-only">Menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={handleClearHistory}>
                    {translate('assistant.clearHistory', 'Clear chat history')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <div>
                <SheetTitle className="text-base">
                  {translate('assistant.title', 'Virtual Assistant')}
                </SheetTitle>
                <SheetDescription className="text-sm">
                  {translate(
                    'assistant.description',
                    'Ask me anything about your store operations'
                  )}
                </SheetDescription>
              </div>
            </div>
          </div>
          {cryptoAvailable && (
            <div className="flex items-center gap-1.5 pt-1.5 border-t">
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              <Label
                htmlFor="encryption-toggle"
                className="text-sm cursor-pointer flex-1"
              >
                {translate(
                  'assistant.encryptHistory',
                  'Encrypt chat history (for public computers)'
                )}
              </Label>
              <Switch
                id="encryption-toggle"
                checked={encryptionEnabled}
                onCheckedChange={handleEncryptionToggle}
              />
            </div>
          )}
        </SheetHeader>

        {/* Messages area */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-2.5 space-y-2"
        >
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center w-full px-4">
                <Bot className="h-8 w-8 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  {(() => {
                    const greetingName = getGreetingName();
                    if (greetingName) {
                      return `${greetingName}, ${translate(
                        'assistant.startConversation',
                        'Start a conversation with your assistant'
                      ).toLowerCase()}`;
                    }
                    return translate(
                      'assistant.startConversation',
                      'Start a conversation with your assistant'
                    );
                  })()}
                </p>
                <p className="text-sm mt-1.5 mb-3">
                  {translate(
                    'assistant.helpMessage',
                    'I can help you create orders, manage inventory, answer questions, and more!'
                  )}
                </p>
                {/* Initial suggestions */}
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  {[
                    translate(
                      'assistant.suggestions.createOrder',
                      'Create an order'
                    ),
                    translate(
                      'assistant.suggestions.createProduct',
                      'Create a product'
                    ),
                    translate(
                      'assistant.suggestions.viewInventory',
                      'View inventory'
                    ),
                    translate(
                      'assistant.suggestions.help',
                      'How can you help?'
                    ),
                  ].map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      className="text-sm"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <Card
                  className={`max-w-[80%] ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <CardContent className="p-2">
                    <div className="flex items-start gap-1.5">
                      {message.role === 'assistant' && (
                        <Bot className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      )}
                      {message.role === 'user' && (
                        <User className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        {/* Display screenshot if present (only for user messages) */}
                        {message.role === 'user' && message.screenshot && (
                          <div className="mb-2 rounded-md overflow-hidden border border-border bg-background">
                            <img
                              src={message.screenshot}
                              alt="Screenshot"
                              className="w-full h-auto max-h-64 object-contain cursor-pointer"
                              onClick={() => {
                                // Open screenshot in new window for full view
                                const newWindow = window.open();
                                if (newWindow) {
                                  newWindow.document.write(
                                    `<img src="${message.screenshot}" style="max-width: 100%; height: auto;" />`
                                  );
                                }
                              }}
                            />
                          </div>
                        )}
                        {message.role === 'assistant' ? (
                          <div className="text-sm leading-relaxed prose prose-base max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: ({ children }) => (
                                  <p className="mb-2 last:mb-0">{children}</p>
                                ),
                                ul: ({ children }) => (
                                  <ul className="list-disc list-inside mb-2 space-y-1">
                                    {children}
                                  </ul>
                                ),
                                ol: ({ children }) => (
                                  <ol className="list-decimal list-inside mb-2 space-y-1">
                                    {children}
                                  </ol>
                                ),
                                li: ({ children }) => (
                                  <li className="ml-2">{children}</li>
                                ),
                                code: ({ className, children, ...props }) => {
                                  const isInline =
                                    !className?.includes('language-');
                                  return isInline ? (
                                    <code
                                      className="bg-muted px-1 py-0.5 rounded text-[11px] font-mono"
                                      {...props}
                                    >
                                      {children}
                                    </code>
                                  ) : (
                                    <code
                                      className="block bg-muted p-2 rounded text-[11px] font-mono overflow-x-auto"
                                      {...props}
                                    >
                                      {children}
                                    </code>
                                  );
                                },
                                strong: ({ children }) => (
                                  <strong className="font-semibold">
                                    {children}
                                  </strong>
                                ),
                                em: ({ children }) => (
                                  <em className="italic">{children}</em>
                                ),
                                blockquote: ({ children }) => (
                                  <blockquote className="border-l-2 border-muted-foreground/30 pl-2 italic my-2">
                                    {children}
                                  </blockquote>
                                ),
                                h1: ({ children }) => (
                                  <h1 className="text-base font-bold mb-1 mt-2 first:mt-0">
                                    {children}
                                  </h1>
                                ),
                                h2: ({ children }) => (
                                  <h2 className="text-sm font-bold mb-1 mt-2 first:mt-0">
                                    {children}
                                  </h2>
                                ),
                                h3: ({ children }) => (
                                  <h3 className="text-sm font-semibold mb-1 mt-2 first:mt-0">
                                    {children}
                                  </h3>
                                ),
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">
                            {message.content}
                          </p>
                        )}

                        {/* Action cards */}
                        {message.actions && message.actions.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {message.actions.map((action, actionIdx) => {
                              if (
                                action.status === 'completed' ||
                                action.status === 'rejected'
                              ) {
                                return null; // Don't show completed/rejected actions
                              }

                              return (
                                <Card
                                  key={actionIdx}
                                  className="bg-background border-2 border-primary/20"
                                >
                                  <CardContent className="p-3">
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <p className="text-sm font-semibold">
                                          {(action as any).title ||
                                            translate(
                                              `assistant.action.${action.entity}`,
                                              `Create ${action.entity}`
                                            )}
                                        </p>
                                        <span className="text-[11px] px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 rounded">
                                          {translate(
                                            'assistant.pending',
                                            'Pending'
                                          )}
                                        </span>
                                      </div>

                                      {/* Display action data */}
                                      {action.data && (
                                        <div className="text-[11px] space-y-1.5 text-muted-foreground">
                                          {action.entity === 'order' &&
                                          action.data.items ? (
                                            // Special formatting for order items
                                            <>
                                              <div className="font-medium text-foreground mb-1">
                                                {translate(
                                                  'assistant.orderItems',
                                                  'Order Items'
                                                )}
                                                :
                                              </div>
                                              {Array.isArray(
                                                action.data.items
                                              ) &&
                                                action.data.items.map(
                                                  (item: any, idx: number) => (
                                                    <div
                                                      key={idx}
                                                      className="ml-2 flex items-center gap-2"
                                                    >
                                                      <span className="text-primary font-medium">
                                                        {item.quantity || 1}x
                                                      </span>
                                                      <span>
                                                        {item.productName ||
                                                          item.name ||
                                                          'Unknown Product'}
                                                      </span>
                                                    </div>
                                                  )
                                                )}
                                              {action.data.customerName && (
                                                <div className="mt-2 pt-2 border-t border-border/50">
                                                  <span className="font-medium">
                                                    {translate(
                                                      'assistant.customer',
                                                      'Customer'
                                                    )}
                                                    :
                                                  </span>
                                                  <span className="ml-2">
                                                    {action.data.customerName}
                                                  </span>
                                                </div>
                                              )}
                                            </>
                                          ) : (
                                            // Generic display for other action types
                                            Object.entries(action.data).map(
                                              ([key, value]) => {
                                                if (
                                                  value === null ||
                                                  value === undefined ||
                                                  value === '' ||
                                                  key === 'customerId' ||
                                                  key === 'productId'
                                                )
                                                  return null;
                                                return (
                                                  <div
                                                    key={key}
                                                    className="flex"
                                                  >
                                                    <span className="font-medium capitalize">
                                                      {key}:
                                                    </span>
                                                    <span className="ml-1">
                                                      {typeof value === 'object'
                                                        ? JSON.stringify(value)
                                                        : String(value)}
                                                    </span>
                                                  </div>
                                                );
                                              }
                                            )
                                          )}
                                        </div>
                                      )}

                                      {/* Approve/Reject buttons */}
                                      <div className="flex gap-2 pt-1">
                                        <Button
                                          size="sm"
                                          className="flex-1 h-7 text-sm"
                                          onClick={() =>
                                            handleApproveAction(
                                              action.actionId ||
                                                `${action.toolName}:${actionIdx}`,
                                              action.toolName || '',
                                              action.toolArgs || action.data
                                            )
                                          }
                                          disabled={loading}
                                        >
                                          <Check className="h-3 w-3 mr-1" />
                                          {translate(
                                            'assistant.approve',
                                            'Approve'
                                          )}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="flex-1 h-7 text-sm"
                                          onClick={() =>
                                            handleRejectAction(
                                              action.actionId ||
                                                `${action.toolName}:${actionIdx}`
                                            )
                                          }
                                          disabled={loading}
                                        >
                                          <X className="h-3 w-3 mr-1" />
                                          {translate(
                                            'assistant.reject',
                                            'Reject'
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        )}

                        {/* Suggestions */}
                        {message.suggestions &&
                          message.suggestions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {message.suggestions.map((suggestion, sugIdx) => (
                                <Button
                                  key={sugIdx}
                                  variant="outline"
                                  size="sm"
                                  className="text-[11px] h-6 px-2"
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                >
                                  {suggestion}
                                </Button>
                              ))}
                            </div>
                          )}

                        <p
                          className={`text-[11px] mt-0.5 ${
                            message.role === 'user'
                              ? 'text-primary-foreground/70'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <Card className="bg-muted">
                <CardContent className="p-2">
                  <div className="flex items-center gap-1.5">
                    <Bot className="h-3.5 w-3.5" />
                    <div className="flex gap-0.5">
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" />
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:0.2s]" />
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:0.4s]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="p-2.5 border-t space-y-1.5">
          <div className="flex gap-1.5">
            <div className="flex-1 relative">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={translate(
                  'assistant.inputPlaceholder',
                  'Ask me anything... Try "create an order" or "show inventory" (Press Enter to send)'
                )}
                className="min-h-[48px] text-sm resize-none pb-10"
                disabled={loading}
              />
              
              {/* Horizontal bar at bottom with microphone button and animations */}
              <div className={`absolute bottom-0 left-0 right-0 h-8 flex items-center justify-center gap-2 px-2 ${
                isRecording 
                  ? 'bg-red-500/10 border-t border-red-500/30' 
                  : 'border-t border-border/50'
              }`}>
                {/* Left side sound waves */}
                {isRecording && (
                  <div className="flex items-center gap-1 h-4 flex-1 justify-end pr-2">
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0s', height: '30%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.15s', height: '50%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.3s', height: '70%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.45s', height: '100%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.6s', height: '80%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.75s', height: '60%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.9s', height: '40%' }} />
                  </div>
                )}
                
                {/* Microphone button */}
                <Button
                  onClick={
                    isRecording ? handleStopRecording : handleStartRecording
                  }
                  disabled={loading}
                  variant={isRecording ? 'destructive' : 'default'}
                  size="icon"
                  className={`h-7 w-7 rounded-full relative overflow-visible ${
                    isRecording 
                      ? 'shadow-lg shadow-red-500/50 animate-pulse' 
                      : 'hover:scale-105 transition-transform'
                  }`}
                  title={
                    isRecording
                      ? translate('assistant.stopRecording', 'Stop recording')
                      : translate(
                          'assistant.startRecording',
                          'Start voice recording'
                        )
                  }
                >
                  {isRecording ? (
                    <Mic className="h-3.5 w-3.5" />
                  ) : (
                    <Mic className="h-3.5 w-3.5" />
                  )}
                </Button>
                
                {/* Right side sound waves */}
                {isRecording && (
                  <div className="flex items-center gap-1 h-4 flex-1 justify-start pl-2">
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0s', height: '40%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.15s', height: '60%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.3s', height: '80%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.45s', height: '100%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.6s', height: '70%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.75s', height: '50%' }} />
                    <span className="w-0.5 bg-red-500 rounded-full animate-mic-wave" style={{ animationDelay: '0.9s', height: '30%' }} />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                size="icon"
                className="h-9 w-9"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
              <Button
                onClick={handleScreenshot}
                disabled={loading}
                variant="outline"
                size="icon"
                className="h-9 w-9"
                title={translate(
                  'assistant.captureScreenshot',
                  'Capture screenshot'
                )}
              >
                <Camera className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
